import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, ScrollText, Plus, Bell, Send, Sparkles, Coins, ArrowUpRight,
  ArrowDownLeft, BarChart3, AlertTriangle, RefreshCw, Calendar, Pencil, Trash2,
  Factory, Home, Briefcase, Lightbulb, ClipboardList, Building2, Shield, Wrench, Pin,
  ChevronLeft, ChevronRight, Wallet, Store
} from 'lucide-react';
import LogModal from '../components/LogModal';
import PriceInput from '../components/PriceInput';
import logService from '../services/logService';
import { API_URL } from '../config/api';
import useEscapeClose from '../hooks/useEscapeClose';
import './PaymentCalendar.css';

const PaymentCalendar = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [originalPayment, setOriginalPayment] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');

  // Takvim state'leri
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarView, setCalendarView] = useState('month'); // 'day', 'month', 'year'

  // Filtre
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [listTab, setListTab] = useState('all'); // all, pending, paid

  // Liste filtreleri
  const [listFilterCategory, setListFilterCategory] = useState('all');
  const [listFilterMonth, setListFilterMonth] = useState('all');
  const [listSearchTerm, setListSearchTerm] = useState('');
  const [listDisplayLimit, setListDisplayLimit] = useState(20);

  // Masraflar (Personnel sayfasından taşındı)
  const [stores, setStores] = useState([]);
  const [allExpenses, setAllExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [expensesStartDate, setExpensesStartDate] = useState(new Date(new Date().setDate(1)).toISOString().split('T')[0]);
  const [expensesEndDate, setExpensesEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [expensesStoreFilter, setExpensesStoreFilter] = useState('all');

  // Modal kapanış animasyonu için
  const [closingModal, setClosingModal] = useState(null);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showModal, () => setShowModal(false)],
    [showNotifications, () => setShowNotifications(false)],
    [showLogModal, () => {}],
  ]);

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Form state'i
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    amount: '',
    currency: 'TRY', // TRY, USD, EUR, GOLD, SILVER
    payment_type: 'expense', // expense (gider) veya income (gelir)
    category: 'supplier', // supplier, rent, salary, utility, tax, other
    recipient: '', // Kime ödenecek
    due_date: '',
    reminder_days: 3, // Kaç gün önce hatırlat
    recurring: false, // Tekrarlayan mı
    recurring_interval: 'monthly', // monthly, weekly, yearly
    status: 'pending', // pending, paid, cancelled
    notes: ''
  });

  // Para birimi isimleri ve sembolleri
  const currencyInfo = {
    TRY: { name: 'Türk Lirası', symbol: '₺', icon: '🇹🇷' },
    USD: { name: 'Amerikan Doları', symbol: '$', icon: '🇺🇸' },
    EUR: { name: 'Euro', symbol: '€', icon: '🇪🇺' },
    GOLD: { name: 'Altın (gram)', symbol: 'gr', icon: '🥇' },
    SILVER: { name: 'Gümüş (gram)', symbol: 'gr', icon: '🥈' }
  };

  // Para birimi formatla
  const formatCurrency = (amount, currency) => {
    const info = currencyInfo[currency] || currencyInfo.TRY;
    if (currency === 'GOLD' || currency === 'SILVER') {
      return `${Number(amount).toLocaleString()} ${info.symbol}`;
    }
    return `${info.symbol}${Number(amount).toLocaleString()}`;
  };

  // Kategori isimleri
  const categoryNames = {
    supplier: 'Tedarikçi',
    rent: 'Kira',
    salary: 'Maaş',
    utility: 'Fatura',
    tax: 'Vergi',
    loan: 'Kredi/Borç',
    insurance: 'Sigorta',
    maintenance: 'Bakım/Onarım',
    other: 'Diğer'
  };

  // Otomatik tekrarlayan kategoriler (aylık)
  const autoRecurringCategories = ['rent', 'salary', 'utility', 'loan', 'insurance'];

  // Kategori ikonları
  const categoryIcons = {
    supplier: '🏭',
    rent: '🏠',
    salary: '💼',
    utility: '💡',
    tax: '📋',
    loan: '🏦',
    insurance: '🛡️',
    maintenance: '🔧',
    other: '📌'
  };

  // Tekrar sıklığı isimleri
  const recurringIntervalNames = {
    weekly: 'Haftalık',
    monthly: 'Aylık',
    yearly: 'Yıllık'
  };

  // Durum isimleri
  const statusNames = {
    pending: 'Bekliyor',
    paid: 'Ödendi',
    cancelled: 'İptal',
    overdue: 'Gecikmiş'
  };

  // Modal kapanış handler'ı
  const handleCloseModal = (modalName, setShowModal) => {
    setClosingModal(modalName);
    setTimeout(() => {
      setShowModal(false);
      setClosingModal(null);
    }, 250);
  };

  useEffect(() => {
    fetchPayments();
    fetchStores();
    // Tarayıcı bildirim izni kontrolü
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Mağazaları getir
  const fetchStores = async () => {
    try {
      const response = await fetch(`${API_URL}/stores`);
      const data = await response.json();
      setStores(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Mağaza yükleme hatası:', error);
    }
  };

  // Masrafları getir
  const fetchAllExpenses = async () => {
    try {
      setLoadingExpenses(true);
      let url = `${API_URL}/expenses?start_date=${expensesStartDate}&end_date=${expensesEndDate}`;
      if (expensesStoreFilter !== 'all') {
        url += `&store_id=${expensesStoreFilter}`;
      }
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setAllExpenses(data.expenses || []);
      }
    } catch (error) {
      console.error('Masraflar yüklenirken hata:', error);
    } finally {
      setLoadingExpenses(false);
    }
  };

  // Filtre değiştikçe masrafları yenile
  useEffect(() => {
    fetchAllExpenses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expensesStartDate, expensesEndDate, expensesStoreFilter]);

  // Hatırlatma gereken ödemeleri al
  const getReminderPayments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return payments
      .filter(p => {
        if (p.status !== 'pending') return false;

        const dueDate = new Date(p.due_date);
        dueDate.setHours(0, 0, 0, 0);

        const diffTime = dueDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        // Vadesi geçmiş veya hatırlatma süresi içinde
        return diffDays <= (p.reminder_days || 3) && diffDays >= -30;
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };

  // Bildirim izni iste
  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        toast.success('Bildirim izni verildi!');
        // Test bildirimi gönder
        new Notification('Ödeme Hatırlatıcı', {
          body: 'Bildirimler aktif edildi. Yaklaşan ödemeleriniz için hatırlatma alacaksınız.',
          icon: '💰'
        });
      }
    }
  };

  // Tarayıcı bildirimi gönder
  const sendBrowserNotification = (payment) => {
    if (notificationPermission === 'granted') {
      const dueDate = new Date(payment.due_date);
      const today = new Date();
      const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

      let message = '';
      if (diffDays < 0) {
        message = `${Math.abs(diffDays)} gün gecikti! Tutar: ${formatCurrency(payment.amount, payment.currency)}`;
      } else if (diffDays === 0) {
        message = `Bugün ödenmesi gerekiyor! Tutar: ${formatCurrency(payment.amount, payment.currency)}`;
      } else {
        message = `${diffDays} gün sonra. Tutar: ${formatCurrency(payment.amount, payment.currency)}`;
      }

      new Notification(payment.title, {
        body: message,
        icon: '💰',
        tag: `payment-${payment.id}`
      });
    }
  };

  // Tüm hatırlatmalar için bildirim gönder
  const sendAllReminders = () => {
    const reminders = getReminderPayments();
    reminders.forEach((payment, index) => {
      setTimeout(() => sendBrowserNotification(payment), index * 1000);
    });
    toast.success(`${reminders.length} hatırlatma bildirimi gönderildi`);
  };

  const fetchPayments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/payments`);
      const data = await response.json();
      setPayments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Ödemeler yüklenemedi:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  // Takvim fonksiyonları
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];

    // Önceki ayın günleri
    for (let i = 0; i < startingDay; i++) {
      const prevDate = new Date(year, month, -startingDay + i + 1);
      days.push({ date: prevDate, isCurrentMonth: false });
    }

    // Bu ayın günleri
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }

    // Sonraki ayın günleri (6 satır tamamlamak için)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Ödemenin vade tarihini yerel Date objesine çevir
  const getPaymentLocalDate = (dueDateStr) => {
    if (!dueDateStr) return null;
    // "2026-01-05T21:00:00.000Z" veya "2026-01-05" formatını işle
    const dateOnly = dueDateStr.split('T')[0];
    const [year, month, day] = dateOnly.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Tekrarlayan ödemeler için gelecek tarihleri hesapla
  const generateRecurringOccurrences = (payment, startDate, endDate) => {
    const occurrences = [];
    const isRecurring = payment.recurring === 1 || payment.recurring === true;

    if (!isRecurring) return occurrences;

    let currentDate = getPaymentLocalDate(payment.due_date);
    if (!currentDate) return occurrences;

    const end = new Date(endDate);
    end.setMonth(end.getMonth() + 12); // 12 ay ileriye kadar hesapla

    while (currentDate <= end) {
      if (currentDate >= startDate) {
        occurrences.push({
          ...payment,
          due_date: formatDate(currentDate),
          isVirtualOccurrence: true,
          originalDueDate: payment.due_date
        });
      }

      // Sonraki tarihi hesapla
      const nextDate = new Date(currentDate);
      switch (payment.recurring_interval) {
        case 'weekly':
          nextDate.setDate(nextDate.getDate() + 7);
          break;
        case 'monthly':
          nextDate.setMonth(nextDate.getMonth() + 1);
          break;
        case 'yearly':
          nextDate.setFullYear(nextDate.getFullYear() + 1);
          break;
        default:
          break;
      }
      currentDate = nextDate;
    }

    return occurrences;
  };

  // Tüm ödemeleri (tekrarlayan dahil) al
  const getAllPaymentsWithRecurring = () => {
    const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);

    const allPayments = [];

    payments.forEach(payment => {
      const isRecurring = payment.recurring === 1 || payment.recurring === true;

      // Ödendi veya iptal edilmiş ödemeleri de göster (ama sanal kayıt oluşturma)
      if (payment.status === 'paid' || payment.status === 'cancelled') {
        allPayments.push(payment);
        return;
      }

      if (isRecurring) {
        // Tekrarlayan ödemeler için sanal kayıtlar oluştur
        const occurrences = generateRecurringOccurrences(payment, startDate, endDate);
        allPayments.push(...occurrences);
      } else {
        // Normal ödemeleri direkt ekle
        allPayments.push(payment);
      }
    });

    return allPayments;
  };

  const getPaymentsForDate = (date) => {
    const dateStr = formatDate(date);
    const allPayments = getAllPaymentsWithRecurring();
    return allPayments.filter(p => {
      if (!p.due_date) return false;
      // Veritabanından gelen tarihi doğru şekilde parse et (timezone sorunu çözümü)
      const paymentDate = getPaymentLocalDate(p.due_date);
      if (!paymentDate) return false;
      const paymentDateStr = formatDate(paymentDate);
      return paymentDateStr === dateStr;
    });
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const prevDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() - 1);
    setCurrentDate(newDate);
  };

  const nextDay = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + 1);
    setCurrentDate(newDate);
  };

  const prevYear = () => {
    setCurrentDate(new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1));
  };

  const nextYear = () => {
    setCurrentDate(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Navigasyon fonksiyonları
  const handlePrev = () => {
    if (calendarView === 'day') prevDay();
    else if (calendarView === 'month') prevMonth();
    else prevYear();
  };

  const handleNext = () => {
    if (calendarView === 'day') nextDay();
    else if (calendarView === 'month') nextMonth();
    else nextYear();
  };

  // Başlık formatı
  const getCalendarTitle = () => {
    if (calendarView === 'day') {
      return `${currentDate.getDate()} ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else if (calendarView === 'month') {
      return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    } else {
      return `${currentDate.getFullYear()}`;
    }
  };

  // Yıllık görünüm için ay verileri
  const getYearData = () => {
    const year = currentDate.getFullYear();
    const months = [];

    for (let month = 0; month < 12; month++) {
      const monthPayments = payments.filter(p => {
        const d = getPaymentLocalDate(p.due_date);
        return d && d.getMonth() === month && d.getFullYear() === year;
      });

      const expense = monthPayments
        .filter(p => p.payment_type === 'expense' && p.status !== 'cancelled')
        .reduce((sum, p) => sum + Number(p.amount), 0);

      const income = monthPayments
        .filter(p => p.payment_type === 'income' && p.status !== 'cancelled')
        .reduce((sum, p) => sum + Number(p.amount), 0);

      const pending = monthPayments.filter(p => p.status === 'pending').length;
      const paid = monthPayments.filter(p => p.status === 'paid').length;

      months.push({
        month,
        name: monthNames[month],
        expense,
        income,
        pending,
        paid,
        total: monthPayments.length
      });
    }

    return months;
  };

  // Günlük görünüm için saatlik ödemeler
  const getDayPayments = () => {
    return getPaymentsForDate(currentDate).sort((a, b) =>
      new Date(a.due_date) - new Date(b.due_date)
    );
  };

  // Yaklaşan ödemeler (7 gün içinde)
  const getUpcomingPayments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);

    return payments
      .filter(p => {
        if (p.status !== 'pending') return false;
        const dueDate = new Date(p.due_date);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate >= today && dueDate <= nextWeek;
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };

  // Gecikmiş ödemeler
  const getOverduePayments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return payments
      .filter(p => {
        if (p.status !== 'pending') return false;
        const dueDate = new Date(p.due_date);
        dueDate.setHours(0, 0, 0, 0);
        return dueDate < today;
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };

  // Ödeme kaydet
  const handleSave = async () => {
    if (!formData.title || !formData.amount || !formData.due_date) {
      toast.warning('Başlık, tutar ve tarih zorunludur!');
      return;
    }

    try {
      const url = editingPayment
        ? `${API_URL}/payments/${editingPayment.id}`
        : `${API_URL}/payments`;
      const method = editingPayment ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          created_by: user?.id
        })
      });

      const data = await response.json();
      if (data.success) {
        if (editingPayment) {
          // Değişiklikleri tespit et
          const changes = [];
          if (originalPayment?.title !== formData.title) {
            changes.push(`Başlık: "${originalPayment?.title}" → "${formData.title}"`);
          }
          if (String(originalPayment?.amount) !== String(formData.amount)) {
            changes.push(`Tutar: ₺${Number(originalPayment?.amount || 0).toLocaleString()} → ₺${Number(formData.amount).toLocaleString()}`);
          }
          if (originalPayment?.due_date?.split('T')[0] !== formData.due_date) {
            changes.push(`Tarih: ${originalPayment?.due_date?.split('T')[0]} → ${formData.due_date}`);
          }
          if (originalPayment?.recipient !== formData.recipient) {
            changes.push(`Alıcı: "${originalPayment?.recipient || '-'}" → "${formData.recipient || '-'}"`);
          }
          if (originalPayment?.category !== formData.category) {
            changes.push(`Kategori: ${categoryNames[originalPayment?.category]} → ${categoryNames[formData.category]}`);
          }
          if (originalPayment?.status !== formData.status) {
            changes.push(`Durum: ${statusNames[originalPayment?.status]} → ${statusNames[formData.status]}`);
          }

          logService.log('payments', 'update',
            `Ödeme güncellendi: ${formData.title} - ${changes.length} değişiklik`, {
            entity_type: 'payment',
            entity_id: editingPayment.id,
            details: {
              baslik: formData.title,
              tutar: `₺${Number(formData.amount).toLocaleString()}`,
              degisiklik_sayisi: changes.length,
              degisiklikler: changes.length > 0 ? changes : ['Değişiklik yapılmadı']
            },
            status: 'success'
          });
        } else {
          logService.log('payments', 'create',
            `Yeni ödeme planı: ${formData.title} - ₺${Number(formData.amount).toLocaleString()}`, {
            entity_type: 'payment',
            entity_id: data.payment?.id,
            details: {
              baslik: formData.title,
              aciklama: formData.description || '-',
              tutar: `₺${Number(formData.amount).toLocaleString()}`,
              tur: formData.payment_type === 'expense' ? 'Gider' : 'Gelir',
              kategori: categoryNames[formData.category],
              alici: formData.recipient || '-',
              vade_tarihi: formData.due_date,
              hatirlatma: `${formData.reminder_days} gün önce`,
              tekrarlayan: formData.recurring ? `Evet (${formData.recurring_interval === 'monthly' ? 'Aylık' : formData.recurring_interval === 'weekly' ? 'Haftalık' : 'Yıllık'})` : 'Hayır'
            },
            status: 'success'
          });
        }

        toast.success(editingPayment ? 'Ödeme güncellendi!' : 'Ödeme planı oluşturuldu!');
        setShowModal(false);
        resetForm();
        fetchPayments();
      } else {
        toast.error(data.error || 'İşlem başarısız!');
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      toast.error('İşlem başarısız!');
    }
  };

  // Ödeme durumunu güncelle
  const updatePaymentStatus = async (payment, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/payments/${payment.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        logService.log('payments', 'update',
          `Ödeme durumu değiştirildi: ${payment.title} → ${statusNames[newStatus]}`, {
          entity_type: 'payment',
          entity_id: payment.id,
          details: {
            baslik: payment.title,
            tutar: `₺${Number(payment.amount).toLocaleString()}`,
            onceki_durum: statusNames[payment.status],
            yeni_durum: statusNames[newStatus],
            vade_tarihi: payment.due_date?.split('T')[0]
          },
          status: 'success'
        });

        toast.success(`Ödeme ${statusNames[newStatus].toLowerCase()} olarak işaretlendi!`);
        fetchPayments();
      }
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      toast.error('Güncelleme başarısız!');
    }
  };

  // Ödeme sil
  const handleDelete = async (payment) => {
    showConfirmation({
      title: 'Ödeme Sil',
      message: `"${payment.title}" ödemesini silmek istediğinize emin misiniz?`,
      confirmText: 'Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/payments/${payment.id}`, {
            method: 'DELETE'
          });

          const data = await response.json();
          if (data.success) {
            logService.log('payments', 'delete',
              `Ödeme silindi: ${payment.title} - ₺${Number(payment.amount).toLocaleString()}`, {
              entity_type: 'payment',
              entity_id: payment.id,
              details: {
                baslik: payment.title,
                tutar: `₺${Number(payment.amount).toLocaleString()}`,
                kategori: categoryNames[payment.category],
                alici: payment.recipient || '-',
                vade_tarihi: payment.due_date?.split('T')[0],
                durum: statusNames[payment.status]
              },
              status: 'success'
            });

            toast.success('Ödeme silindi!');
            fetchPayments();
          }
        } catch (error) {
          console.error('Silme hatası:', error);
          toast.error('Silme başarısız!');
        }
      }
    });
  };

  const openEditModal = (payment) => {
    setEditingPayment(payment);
    const formValues = {
      title: payment.title || '',
      description: payment.description || '',
      amount: payment.amount || '',
      currency: payment.currency || 'TRY',
      payment_type: payment.payment_type || 'expense',
      category: payment.category || 'other',
      recipient: payment.recipient || '',
      due_date: payment.due_date ? payment.due_date.split('T')[0] : '',
      reminder_days: payment.reminder_days || 3,
      recurring: payment.recurring === 1 || payment.recurring === true,
      recurring_interval: payment.recurring_interval || 'monthly',
      status: payment.status || 'pending',
      notes: payment.notes || ''
    };
    setOriginalPayment({ ...formValues });
    setFormData(formValues);
    setShowModal(true);
  };

  const openNewModal = (date = null) => {
    resetForm();
    if (date) {
      setFormData(prev => ({ ...prev, due_date: formatDate(date) }));
    }
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingPayment(null);
    setOriginalPayment(null);
    setFormData({
      title: '',
      description: '',
      amount: '',
      currency: 'TRY',
      payment_type: 'expense',
      category: 'supplier',
      recipient: '',
      due_date: '',
      reminder_days: 3,
      recurring: false,
      recurring_interval: 'monthly',
      status: 'pending',
      notes: ''
    });
  };

  // Filtrelenmiş ödemeler
  const getFilteredPayments = () => {
    return payments.filter(p => {
      if (filterStatus !== 'all' && p.status !== filterStatus) return false;
      if (filterType !== 'all' && p.payment_type !== filterType) return false;
      return true;
    });
  };

  // Liste için filtrelenmiş ödemeler
  const getListFilteredPayments = () => {
    return payments
      .filter(p => {
        // Tab filtresi
        if (listTab === 'pending' && p.status !== 'pending') return false;
        if (listTab === 'paid' && p.status !== 'paid') return false;

        // Kategori filtresi
        if (listFilterCategory !== 'all' && p.category !== listFilterCategory) return false;

        // Ay filtresi
        if (listFilterMonth !== 'all') {
          const paymentDate = getPaymentLocalDate(p.due_date);
          if (!paymentDate || paymentDate.getMonth() !== parseInt(listFilterMonth)) return false;
        }

        // Arama filtresi
        if (listSearchTerm) {
          const searchLower = listSearchTerm.toLowerCase();
          const matchesTitle = p.title?.toLowerCase().includes(searchLower);
          const matchesRecipient = p.recipient?.toLowerCase().includes(searchLower);
          const matchesCategory = categoryNames[p.category]?.toLowerCase().includes(searchLower);
          if (!matchesTitle && !matchesRecipient && !matchesCategory) return false;
        }

        return true;
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };

  // Filtreleri sıfırla
  const resetListFilters = () => {
    setListFilterCategory('all');
    setListFilterMonth('all');
    setListSearchTerm('');
    setListDisplayLimit(20);
  };

  // Kategori değiştiğinde otomatik tekrarlama ayarla
  const handleCategoryChange = (category) => {
    const shouldAutoRecur = autoRecurringCategories.includes(category);
    setFormData(prev => ({
      ...prev,
      category,
      recurring: shouldAutoRecur ? true : prev.recurring,
      recurring_interval: shouldAutoRecur ? 'monthly' : prev.recurring_interval
    }));
  };

  // Sonraki ödeme tarihini hesapla
  const getNextOccurrence = (dueDate, interval) => {
    const date = new Date(dueDate);
    switch (interval) {
      case 'weekly':
        date.setDate(date.getDate() + 7);
        break;
      case 'monthly':
        date.setMonth(date.getMonth() + 1);
        break;
      case 'yearly':
        date.setFullYear(date.getFullYear() + 1);
        break;
      default:
        break;
    }
    return date;
  };

  // İstatistikler
  const getStatistics = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const thisMonth = currentDate.getMonth();
    const thisYear = currentDate.getFullYear();

    const monthPayments = payments.filter(p => {
      const d = new Date(p.due_date);
      return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
    });

    const totalExpense = monthPayments
      .filter(p => p.payment_type === 'expense' && p.status !== 'cancelled')
      .reduce((sum, p) => sum + Number(p.amount), 0);

    const totalIncome = monthPayments
      .filter(p => p.payment_type === 'income' && p.status !== 'cancelled')
      .reduce((sum, p) => sum + Number(p.amount), 0);

    const paidExpense = monthPayments
      .filter(p => p.payment_type === 'expense' && p.status === 'paid')
      .reduce((sum, p) => sum + Number(p.amount), 0);

    const paidIncome = monthPayments
      .filter(p => p.payment_type === 'income' && p.status === 'paid')
      .reduce((sum, p) => sum + Number(p.amount), 0);

    const pendingCount = payments.filter(p => p.status === 'pending').length;
    const overdueCount = getOverduePayments().length;
    const recurringCount = payments.filter(p => p.recurring === 1 || p.recurring === true).length;

    // Kategori bazlı harcamalar
    const categoryBreakdown = {};
    monthPayments
      .filter(p => p.payment_type === 'expense' && p.status !== 'cancelled')
      .forEach(p => {
        categoryBreakdown[p.category] = (categoryBreakdown[p.category] || 0) + Number(p.amount);
      });

    // Ödeme tamamlanma oranı
    const completedPayments = monthPayments.filter(p => p.status === 'paid').length;
    const totalPayments = monthPayments.filter(p => p.status !== 'cancelled').length;
    const completionRate = totalPayments > 0 ? Math.round((completedPayments / totalPayments) * 100) : 0;

    return {
      totalExpense,
      totalIncome,
      paidExpense,
      paidIncome,
      pendingCount,
      overdueCount,
      recurringCount,
      categoryBreakdown,
      completionRate,
      cashFlow: totalIncome - totalExpense,
      paidCashFlow: paidIncome - paidExpense
    };
  };

  const stats = getStatistics();

  const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
  ];

  const dayNames = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

  const upcomingPayments = getUpcomingPayments();
  const overduePayments = getOverduePayments();
  const calendarDays = getDaysInMonth(currentDate);

  return (
    <div className="payment-calendar-container">
      <header className="page-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/')}>
            <ArrowLeft size={18} /> Ana Sayfa
          </button>
          <h1>Ödeme Takip Sistemi</h1>
        </div>
        <div className="header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-buttons">
          {/* Bildirim Zili */}
          <div className="notification-wrapper">
            <button
              className={`notification-bell-btn ${getReminderPayments().length > 0 ? 'has-notifications' : ''}`}
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <Bell size={20} />
              {getReminderPayments().length > 0 && (
                <span className="notification-badge">{getReminderPayments().length}</span>
              )}
            </button>

            {/* Bildirim Dropdown */}
            {showNotifications && (
              <div className="notification-dropdown">
                <div className="notification-header">
                  <h4>Hatırlatmalar</h4>
                  <div className="notification-actions">
                    {notificationPermission !== 'granted' ? (
                      <button className="enable-notifications-btn" onClick={requestNotificationPermission}>
                        <Bell size={14} /> Bildirimleri Aç
                      </button>
                    ) : (
                      <button className="send-all-btn" onClick={sendAllReminders}>
                        <Send size={14} /> Tümünü Bildir
                      </button>
                    )}
                  </div>
                </div>

                <div className="notification-list">
                  {getReminderPayments().length === 0 ? (
                    <div className="no-notifications">
                      <Sparkles size={32} style={{ color: '#f59e0b' }} />
                      <p>Yaklaşan ödeme yok</p>
                    </div>
                  ) : (
                    getReminderPayments().map(payment => {
                      const dueDate = new Date(payment.due_date);
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                      const isOverdue = diffDays < 0;
                      const isToday = diffDays === 0;

                      return (
                        <div
                          key={payment.id}
                          className={`notification-item ${isOverdue ? 'overdue' : ''} ${isToday ? 'today' : ''}`}
                          onClick={() => {
                            openEditModal(payment);
                            setShowNotifications(false);
                          }}
                        >
                          <div className="notification-icon">
                            {categoryIcons[payment.category]}
                          </div>
                          <div className="notification-content">
                            <div className="notification-title">{payment.title}</div>
                            <div className="notification-meta">
                              <span className="notification-amount">
                                {formatCurrency(payment.amount, payment.currency)}
                              </span>
                              <span className={`notification-date ${isOverdue ? 'overdue' : ''} ${isToday ? 'today' : ''}`}>
                                {isOverdue
                                  ? `${Math.abs(diffDays)} gün gecikti`
                                  : isToday
                                    ? 'Bugün'
                                    : `${diffDays} gün sonra`}
                              </span>
                            </div>
                          </div>
                          <div className="notification-quick-actions">
                            <button
                              className="quick-pay-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                updatePaymentStatus(payment, 'paid');
                                setShowNotifications(false);
                              }}
                              title="Ödendi olarak işaretle"
                            >
                              ✓
                            </button>
                            {notificationPermission === 'granted' && (
                              <button
                                className="quick-notify-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  sendBrowserNotification(payment);
                                  toast.info('Bildirim gönderildi');
                                }}
                                title="Bildirim gönder"
                              >
                                <Bell size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {notificationPermission === 'granted' && (
                  <div className="notification-footer">
                    <span className="notification-status">✓ Bildirimler aktif</span>
                  </div>
                )}
              </div>
            )}
          </div>

          <button className="action-header-btn log-btn" onClick={() => setShowLogModal(true)}>
            <ScrollText size={18} /> İşlem Geçmişi
          </button>
          <button className="action-header-btn" onClick={() => openNewModal()}>
            <Plus size={18} /> Yeni Ödeme
          </button>
        </div>
      </header>

      {/* Dashboard - Key Metrics */}
      <div className="payment-dashboard">
        <div className="dashboard-card primary">
          <div className="dashboard-icon"><Coins size={28} style={{ color: '#f59e0b' }} /></div>
          <div className="dashboard-info">
            <span className="dashboard-label">Nakit Akışı</span>
            <span className={`dashboard-value ${stats.cashFlow >= 0 ? 'positive' : 'negative'}`}>
              {stats.cashFlow >= 0 ? '+' : ''}₺{stats.cashFlow.toLocaleString()}
            </span>
            <span className="dashboard-sub">Bu ay beklenen</span>
          </div>
        </div>

        <div className="dashboard-card expense">
          <div className="dashboard-icon"><ArrowUpRight size={28} style={{ color: '#ef4444' }} /></div>
          <div className="dashboard-info">
            <span className="dashboard-label">Toplam Gider</span>
            <span className="dashboard-value">₺{stats.totalExpense.toLocaleString()}</span>
            <span className="dashboard-sub">Ödenen: ₺{stats.paidExpense.toLocaleString()}</span>
          </div>
        </div>

        <div className="dashboard-card income">
          <div className="dashboard-icon"><ArrowDownLeft size={28} style={{ color: '#10b981' }} /></div>
          <div className="dashboard-info">
            <span className="dashboard-label">Toplam Gelir</span>
            <span className="dashboard-value">₺{stats.totalIncome.toLocaleString()}</span>
            <span className="dashboard-sub">Tahsil: ₺{stats.paidIncome.toLocaleString()}</span>
          </div>
        </div>

        <div className="dashboard-card progress">
          <div className="dashboard-icon"><BarChart3 size={28} style={{ color: '#8b5cf6' }} /></div>
          <div className="dashboard-info">
            <span className="dashboard-label">Tamamlanma</span>
            <div className="progress-bar-container">
              <div className="progress-bar" style={{ width: `${stats.completionRate}%` }}></div>
            </div>
            <span className="dashboard-sub">%{stats.completionRate} ödeme tamamlandı</span>
          </div>
        </div>

        <div className="dashboard-card warning">
          <div className="dashboard-icon"><AlertTriangle size={28} style={{ color: '#f59e0b' }} /></div>
          <div className="dashboard-info">
            <span className="dashboard-label">Bekleyen</span>
            <span className="dashboard-value">{stats.pendingCount}</span>
            <span className="dashboard-sub">{stats.overdueCount} gecikmiş</span>
          </div>
        </div>

        <div className="dashboard-card recurring">
          <div className="dashboard-icon"><RefreshCw size={28} style={{ color: '#3b82f6' }} /></div>
          <div className="dashboard-info">
            <span className="dashboard-label">Tekrarlayan</span>
            <span className="dashboard-value">{stats.recurringCount}</span>
            <span className="dashboard-sub">Otomatik ödeme</span>
          </div>
        </div>
      </div>

      <div className="payment-content">
        {/* Sol Panel - Uyarılar ve Liste */}
        <div className="payment-sidebar">
          {/* Gecikmiş Ödemeler Uyarısı */}
          {overduePayments.length > 0 && (
            <div className="alert-card overdue">
              <div className="alert-header">
                <span className="alert-icon"><AlertTriangle size={20} style={{ color: '#ef4444' }} /></span>
                <h3>Gecikmiş Ödemeler ({overduePayments.length})</h3>
              </div>
              <div className="alert-list">
                {overduePayments.slice(0, 3).map(payment => (
                  <div key={payment.id} className="alert-item" onClick={() => openEditModal(payment)}>
                    <div className="alert-item-info">
                      <span className="alert-item-title">{payment.title}</span>
                      <span className="alert-item-recipient">{payment.recipient || categoryNames[payment.category]}</span>
                    </div>
                    <div className="alert-item-amount">{formatCurrency(payment.amount, payment.currency)}</div>
                  </div>
                ))}
                {overduePayments.length > 3 && (
                  <div className="alert-more">+{overduePayments.length - 3} daha</div>
                )}
              </div>
            </div>
          )}

          {/* Yaklaşan Ödemeler */}
          {upcomingPayments.length > 0 && (
            <div className="alert-card upcoming">
              <div className="alert-header">
                <span className="alert-icon"><Calendar size={20} style={{ color: '#3b82f6' }} /></span>
                <h3>Yaklaşan Ödemeler ({upcomingPayments.length})</h3>
              </div>
              <div className="alert-list">
                {upcomingPayments.slice(0, 5).map(payment => {
                  const dueDate = new Date(payment.due_date);
                  const today = new Date();
                  const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

                  return (
                    <div key={payment.id} className="alert-item" onClick={() => openEditModal(payment)}>
                      <div className="alert-item-info">
                        <span className="alert-item-title">{payment.title}</span>
                        <span className="alert-item-date">
                          {diffDays === 0 ? 'Bugün' : diffDays === 1 ? 'Yarın' : `${diffDays} gün sonra`}
                        </span>
                      </div>
                      <div className="alert-item-amount">{formatCurrency(payment.amount, payment.currency)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Filtreler */}
          <div className="filter-card">
            <h3>Filtreler</h3>
            <div className="filter-group">
              <label>Durum</label>
              <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="all">Tümü</option>
                <option value="pending">Bekliyor</option>
                <option value="paid">Ödendi</option>
                <option value="cancelled">İptal</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Tür</label>
              <select value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="all">Tümü</option>
                <option value="expense">Gider</option>
                <option value="income">Gelir</option>
              </select>
            </div>
          </div>

          {/* Kategori Dağılımı - Pasta Grafik */}
          <div className="summary-card category-breakdown">
            {Object.keys(stats.categoryBreakdown).length > 0 ? (
              <div className="pie-chart-container">
                <div className="pie-chart-wrapper">
                  <svg viewBox="0 0 100 100" className="pie-chart">
                    {(() => {
                      const categories = Object.entries(stats.categoryBreakdown)
                        .sort((a, b) => b[1] - a[1]);
                      const colors = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];
                      let currentAngle = 0;

                      return categories.map(([category, amount], index) => {
                        const percentage = (amount / stats.totalExpense) * 100;
                        const angle = (percentage / 100) * 360;
                        const startAngle = currentAngle;
                        const endAngle = currentAngle + angle;
                        currentAngle = endAngle;

                        const startRad = (startAngle - 90) * (Math.PI / 180);
                        const endRad = (endAngle - 90) * (Math.PI / 180);

                        const x1 = 50 + 40 * Math.cos(startRad);
                        const y1 = 50 + 40 * Math.sin(startRad);
                        const x2 = 50 + 40 * Math.cos(endRad);
                        const y2 = 50 + 40 * Math.sin(endRad);

                        const largeArc = angle > 180 ? 1 : 0;

                        const pathD = `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;

                        return (
                          <path
                            key={category}
                            d={pathD}
                            fill={colors[index % colors.length]}
                            stroke="#fff"
                            strokeWidth="0.5"
                            className="pie-slice"
                          >
                            <title>{categoryNames[category]}: ₺{Number(amount).toLocaleString()} (%{percentage.toFixed(1)})</title>
                          </path>
                        );
                      });
                    })()}
                    <circle cx="50" cy="50" r="22" fill="#fff" />
                  </svg>
                  <div className="pie-chart-title">
                    <h4>Kategoriler</h4>
                    <div className="pie-total">
                      ₺{stats.totalExpense > 999999
                        ? (stats.totalExpense / 1000000).toFixed(1) + 'M'
                        : stats.totalExpense > 999
                          ? (stats.totalExpense / 1000).toFixed(1) + 'K'
                          : stats.totalExpense.toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="pie-legend">
                  {Object.entries(stats.categoryBreakdown)
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, amount], index) => {
                      const colors = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1'];
                      const percentage = ((amount / stats.totalExpense) * 100).toFixed(1);
                      return (
                        <div key={category} className="pie-legend-item">
                          <span className="pie-legend-color" style={{ background: colors[index % colors.length] }}></span>
                          <span className="pie-legend-icon">{categoryIcons[category]}</span>
                          <span className="pie-legend-name">{categoryNames[category]}</span>
                          <span className="pie-legend-value">%{percentage}</span>
                        </div>
                      );
                    })}
                </div>
              </div>
            ) : (
              <div className="no-data">Bu ay gider kaydı yok</div>
            )}
          </div>

          {/* Tekrarlayan Ödemeler - Pasta Grafik */}
          <div className="summary-card recurring-summary">
            {(() => {
              const allRecurring = payments.filter(p => (p.recurring === 1 || p.recurring === true) && p.status !== 'cancelled');
              const totalRecurring = allRecurring.reduce((sum, p) => sum + Number(p.amount), 0);
              // En yüksek 5 ödemeyi al, kalanları "Diğer" olarak grupla
              const sortedPayments = [...allRecurring].sort((a, b) => Number(b.amount) - Number(a.amount));
              const top5 = sortedPayments.slice(0, 5);
              const others = sortedPayments.slice(5);
              const othersTotal = others.reduce((sum, p) => sum + Number(p.amount), 0);
              const recurringPayments = othersTotal > 0
                ? [...top5, { id: 'others', title: 'Diğer', amount: othersTotal, category: 'other', currency: 'TRY', isOthers: true }]
                : top5;

              if (recurringPayments.length === 0) {
                return <div className="no-data">Tekrarlayan ödeme yok</div>;
              }

              const colors = ['#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#ef4444', '#22c55e', '#3b82f6', '#f59e0b'];
              let currentAngle = 0;

              return (
                <div className="pie-chart-container">
                  <div className="pie-chart-wrapper">
                    <svg viewBox="0 0 100 100" className="pie-chart">
                      {recurringPayments.map((payment, index) => {
                        const percentage = (Number(payment.amount) / totalRecurring) * 100;
                        const angle = (percentage / 100) * 360;
                        const startAngle = currentAngle;
                        const endAngle = currentAngle + angle;
                        currentAngle = endAngle;

                        const startRad = (startAngle - 90) * (Math.PI / 180);
                        const endRad = (endAngle - 90) * (Math.PI / 180);

                        const x1 = 50 + 40 * Math.cos(startRad);
                        const y1 = 50 + 40 * Math.sin(startRad);
                        const x2 = 50 + 40 * Math.cos(endRad);
                        const y2 = 50 + 40 * Math.sin(endRad);

                        const largeArc = angle > 180 ? 1 : 0;
                        const pathD = `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`;

                        return (
                          <path
                            key={payment.id}
                            d={pathD}
                            fill={colors[index % colors.length]}
                            stroke="#fff"
                            strokeWidth="0.5"
                            className="pie-slice"
                          >
                            <title>{payment.title}: {formatCurrency(payment.amount, payment.currency)} (%{percentage.toFixed(1)})</title>
                          </path>
                        );
                      })}
                      <circle cx="50" cy="50" r="22" fill="#fff" />
                    </svg>
                    <div className="pie-chart-title">
                      <h4>Tekrarlayan</h4>
                      <div className="pie-total">
                        ₺{totalRecurring > 999999
                          ? (totalRecurring / 1000000).toFixed(1) + 'M'
                          : totalRecurring > 999
                            ? (totalRecurring / 1000).toFixed(1) + 'K'
                            : totalRecurring.toLocaleString()}
                      </div>
                    </div>
                  </div>
                  <div className="pie-legend">
                    {recurringPayments.map((payment, index) => {
                      const percentage = ((Number(payment.amount) / totalRecurring) * 100).toFixed(1);
                      return (
                        <div
                          key={payment.id}
                          className={`pie-legend-item ${payment.isOthers ? 'others' : ''}`}
                          onClick={() => !payment.isOthers && openEditModal(payment)}
                        >
                          <span className="pie-legend-color" style={{ background: colors[index % colors.length] }}></span>
                          <span className="pie-legend-icon">{categoryIcons[payment.category]}</span>
                          <span className="pie-legend-name">{payment.title}</span>
                          <span className="pie-legend-value">%{percentage}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* Sağ Panel - Takvim */}
        <div className="calendar-section">
          <div className="calendar-header">
            <div className="calendar-nav">
              <button className="cal-nav-btn" onClick={handlePrev}><ChevronLeft size={20} /></button>
              <h2>{getCalendarTitle()}</h2>
              <button className="cal-nav-btn" onClick={handleNext}><ChevronRight size={20} /></button>
            </div>
            <div className="calendar-view-switcher">
              <button
                className={`view-btn ${calendarView === 'day' ? 'active' : ''}`}
                onClick={() => setCalendarView('day')}
              >
                Gün
              </button>
              <button
                className={`view-btn ${calendarView === 'month' ? 'active' : ''}`}
                onClick={() => setCalendarView('month')}
              >
                Ay
              </button>
              <button
                className={`view-btn ${calendarView === 'year' ? 'active' : ''}`}
                onClick={() => setCalendarView('year')}
              >
                Yıl
              </button>
            </div>
            <button className="today-btn" onClick={goToToday}>Bugün</button>
          </div>

          {/* Günlük Görünüm */}
          {calendarView === 'day' && (
            <div className="day-view">
              <div className="day-view-header">
                <span className="day-view-weekday">
                  {['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'][currentDate.getDay()]}
                </span>
                <button className="add-payment-btn" onClick={() => openNewModal(currentDate)}>
                  + Ödeme Ekle
                </button>
              </div>
              <div className="day-view-payments">
                {getDayPayments().length === 0 ? (
                  <div className="no-payments-day">
                    <Calendar size={48} style={{ color: '#9ca3af' }} />
                    <p>Bu tarihte ödeme bulunmuyor</p>
                    <button className="add-payment-btn" onClick={() => openNewModal(currentDate)}>
                      <Plus size={16} /> Yeni Ödeme Ekle
                    </button>
                  </div>
                ) : (
                  getDayPayments().map(payment => {
                    const isRecurring = payment.recurring === 1 || payment.recurring === true;
                    return (
                      <div
                        key={payment.id + (payment.isVirtualOccurrence ? '-virtual' : '')}
                        className={`day-view-payment ${payment.payment_type} ${payment.status}`}
                      >
                        <div className="day-payment-left">
                          <span className="day-payment-icon">{categoryIcons[payment.category]}</span>
                          <div className="day-payment-info">
                            <h4>
                              {payment.title}
                              {isRecurring && (
                                <span className="recurring-badge"><RefreshCw size={12} /> {recurringIntervalNames[payment.recurring_interval]}</span>
                              )}
                              {payment.isVirtualOccurrence && (
                                <span className="virtual-badge">Planlanan</span>
                              )}
                            </h4>
                            <span className="day-payment-recipient">
                              {payment.recipient || categoryNames[payment.category]}
                            </span>
                            {payment.description && (
                              <p className="day-payment-desc">{payment.description}</p>
                            )}
                          </div>
                        </div>
                        <div className="day-payment-right">
                          <span className={`day-payment-amount ${payment.payment_type}`}>
                            {payment.payment_type === 'expense' ? '-' : '+'}{formatCurrency(payment.amount, payment.currency)}
                          </span>
                          <span className={`status-badge ${payment.status}`}>{statusNames[payment.status]}</span>
                          {payment.status === 'pending' && !payment.isVirtualOccurrence && (
                            <button
                              className="action-btn paid"
                              onClick={() => updatePaymentStatus(payment, 'paid')}
                            >
                              ✓ Ödendi
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {/* Aylık Görünüm */}
          {calendarView === 'month' && (
            <div className="calendar-grid">
              {dayNames.map(day => (
                <div key={day} className="calendar-day-name">{day}</div>
              ))}

              {calendarDays.map((day, index) => {
                const dayPayments = getPaymentsForDate(day.date);
                const isToday = formatDate(day.date) === formatDate(new Date());
                const isSelected = selectedDate && formatDate(day.date) === formatDate(selectedDate);
                const hasOverdue = dayPayments.some(p => p.status === 'pending' && new Date(p.due_date) < new Date());

                return (
                  <div
                    key={index}
                    className={`calendar-day ${!day.isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''} ${hasOverdue ? 'has-overdue' : ''}`}
                    onClick={() => {
                      setSelectedDate(day.date);
                      if (dayPayments.length === 0) {
                        openNewModal(day.date);
                      }
                    }}
                  >
                    <span className="day-number">{day.date.getDate()}</span>
                    {dayPayments.length > 0 && (
                      <div className="day-payments">
                        {dayPayments.slice(0, 2).map(payment => {
                          const isRecurring = payment.recurring === 1 || payment.recurring === true;
                          return (
                            <div
                              key={payment.id + (payment.isVirtualOccurrence ? '-virtual' : '')}
                              className={`day-payment ${payment.payment_type} ${payment.status} ${isRecurring ? 'recurring' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!payment.isVirtualOccurrence) {
                                  openEditModal(payment);
                                }
                              }}
                              title={`${payment.title} - ${formatCurrency(payment.amount, payment.currency)}${isRecurring ? ' (Tekrarlayan)' : ''}${payment.isVirtualOccurrence ? ' (Planlanan)' : ''}`}
                            >
                              <span className="payment-dot"></span>
                              <span className="payment-title">{payment.title}</span>
                              {isRecurring && <span className="recurring-badge-small"><RefreshCw size={10} /></span>}
                            </div>
                          );
                        })}
                        {dayPayments.length > 2 && (
                          <div className="more-payments">+{dayPayments.length - 2}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Yıllık Görünüm */}
          {calendarView === 'year' && (
            <div className="year-view">
              {getYearData().map(monthData => (
                <div
                  key={monthData.month}
                  className={`year-month-card ${monthData.month === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear() ? 'current' : ''}`}
                  onClick={() => {
                    setCurrentDate(new Date(currentDate.getFullYear(), monthData.month, 1));
                    setCalendarView('month');
                  }}
                >
                  <div className="year-month-header">
                    <h3>{monthData.name}</h3>
                    {monthData.total > 0 && (
                      <span className="year-month-count">{monthData.total} ödeme</span>
                    )}
                  </div>
                  <div className="year-month-stats">
                    {monthData.expense > 0 && (
                      <div className="year-stat expense">
                        <span className="year-stat-label">Gider</span>
                        <span className="year-stat-value">₺{monthData.expense.toLocaleString()}</span>
                      </div>
                    )}
                    {monthData.income > 0 && (
                      <div className="year-stat income">
                        <span className="year-stat-label">Gelir</span>
                        <span className="year-stat-value">₺{monthData.income.toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                  {monthData.total > 0 && (
                    <div className="year-month-badges">
                      {monthData.pending > 0 && (
                        <span className="year-badge pending">{monthData.pending} bekleyen</span>
                      )}
                      {monthData.paid > 0 && (
                        <span className="year-badge paid">{monthData.paid} ödenen</span>
                      )}
                    </div>
                  )}
                  {monthData.total === 0 && (
                    <div className="year-month-empty">Ödeme yok</div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Seçili Gün Detayları (sadece ay görünümünde) */}
          {calendarView === 'month' && selectedDate && (
            <div className="selected-day-details">
              <div className="selected-day-header">
                <h3>
                  {selectedDate.getDate()} {monthNames[selectedDate.getMonth()]} {selectedDate.getFullYear()}
                </h3>
                <button className="add-payment-btn" onClick={() => openNewModal(selectedDate)}>
                  + Ödeme Ekle
                </button>
              </div>
              <div className="selected-day-payments">
                {getPaymentsForDate(selectedDate).length === 0 ? (
                  <p className="no-payments">Bu tarihte ödeme yok</p>
                ) : (
                  getPaymentsForDate(selectedDate).map(payment => (
                    <div key={payment.id} className={`payment-item ${payment.payment_type} ${payment.status}`}>
                      <div className="payment-item-main">
                        <div className="payment-item-info">
                          <h4>{payment.title}</h4>
                          <span className="payment-recipient">{payment.recipient || categoryNames[payment.category]}</span>
                        </div>
                        <div className="payment-item-amount">
                          <span className={payment.payment_type}>
                            {payment.payment_type === 'expense' ? '-' : '+'}{formatCurrency(payment.amount, payment.currency)}
                          </span>
                          <span className={`status-badge ${payment.status}`}>{statusNames[payment.status]}</span>
                        </div>
                      </div>
                      <div className="payment-item-actions">
                        {payment.status === 'pending' && (
                          <button className="action-btn paid" onClick={() => updatePaymentStatus(payment, 'paid')}>
                            ✓ Ödendi
                          </button>
                        )}
                        <button className="action-btn edit" onClick={() => openEditModal(payment)}>
                          <Pencil size={14} />
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(payment)}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Ödeme Listesi */}
      <div className="payment-list-section" style={{ margin: '0 30px 30px 30px', maxWidth: '1540px' }}>
        <div className="list-header">
          <h3>Tüm Ödemeler</h3>
          <div className="list-tabs">
            <button
              className={`list-tab ${listTab === 'all' ? 'active' : ''}`}
              onClick={() => { setListTab('all'); setListDisplayLimit(20); }}
            >
              Tümü ({payments.length})
            </button>
            <button
              className={`list-tab ${listTab === 'pending' ? 'active' : ''}`}
              onClick={() => { setListTab('pending'); setListDisplayLimit(20); }}
            >
              Bekleyen ({payments.filter(p => p.status === 'pending').length})
            </button>
            <button
              className={`list-tab ${listTab === 'paid' ? 'active' : ''}`}
              onClick={() => { setListTab('paid'); setListDisplayLimit(20); }}
            >
              Ödenen ({payments.filter(p => p.status === 'paid').length})
            </button>
          </div>
        </div>

        {/* Filtreler */}
        <div className="list-filters">
          <div className="list-search">
            <input
              type="text"
              placeholder="Ödeme ara... (başlık, alıcı, kategori)"
              value={listSearchTerm}
              onChange={(e) => { setListSearchTerm(e.target.value); setListDisplayLimit(20); }}
            />
            {listSearchTerm && (
              <button className="search-clear" onClick={() => setListSearchTerm('')}>✕</button>
            )}
          </div>
          <div className="list-filter-selects">
            <select
              value={listFilterCategory}
              onChange={(e) => { setListFilterCategory(e.target.value); setListDisplayLimit(20); }}
            >
              <option value="all">Tüm Kategoriler</option>
              {Object.entries(categoryNames).map(([key, name]) => (
                <option key={key} value={key}>{categoryIcons[key]} {name}</option>
              ))}
            </select>
            <select
              value={listFilterMonth}
              onChange={(e) => { setListFilterMonth(e.target.value); setListDisplayLimit(20); }}
            >
              <option value="all">Tüm Aylar</option>
              {monthNames.map((name, index) => (
                <option key={index} value={index}>{name}</option>
              ))}
            </select>
            {(listFilterCategory !== 'all' || listFilterMonth !== 'all' || listSearchTerm) && (
              <button className="filter-reset-btn" onClick={resetListFilters}>
                Filtreleri Temizle
              </button>
            )}
          </div>
        </div>

        {/* Sonuç sayısı */}
        <div className="list-results-info">
          <span>{getListFilteredPayments().length} ödeme bulundu</span>
          {getListFilteredPayments().length > listDisplayLimit && (
            <span className="showing-info">
              (İlk {Math.min(listDisplayLimit, getListFilteredPayments().length)} gösteriliyor)
            </span>
          )}
        </div>

        <div className="payment-list">
          {getListFilteredPayments()
            .slice(0, listDisplayLimit)
            .map(payment => {
              const dueDate = new Date(payment.due_date);
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const isOverdue = payment.status === 'pending' && dueDate < today;

              return (
                <div
                  key={payment.id}
                  className={`payment-list-item ${isOverdue ? 'overdue' : ''} ${payment.status} ${(payment.recurring === 1 || payment.recurring === true) ? 'is-recurring' : ''}`}
                >
                  <div className="list-item-left">
                    <div className={`list-item-icon ${payment.payment_type}`}>
                      {categoryIcons[payment.category] || (payment.payment_type === 'expense' ? '📤' : '📥')}
                    </div>
                    <div className="list-item-info">
                      <div className="list-item-title">
                        {payment.title}
                        {(payment.recurring === 1 || payment.recurring === true) && (
                          <span className="recurring-badge" title={`${recurringIntervalNames[payment.recurring_interval]} tekrar`}>
                            <RefreshCw size={12} /> {recurringIntervalNames[payment.recurring_interval]}
                          </span>
                        )}
                      </div>
                      <div className="list-item-meta">
                        <span>{payment.recipient || categoryNames[payment.category]}</span>
                        <span>•</span>
                        <span className={`status-badge ${payment.status}`}>
                          {statusNames[payment.status]}
                        </span>
                        {(payment.recurring === 1 || payment.recurring === true) && payment.status === 'pending' && (
                          <>
                            <span>•</span>
                            <span className="next-date">
                              Sonraki: {getNextOccurrence(payment.due_date, payment.recurring_interval).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="list-item-right">
                    <div className="list-item-date">
                      {dueDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                    <div className={`list-item-amount ${payment.payment_type}`}>
                      {payment.payment_type === 'expense' ? '-' : '+'}{formatCurrency(payment.amount, payment.currency)}
                    </div>
                    <div className="list-item-actions">
                      {payment.status === 'pending' && (
                        <button
                          className="list-action-btn paid-btn"
                          onClick={() => updatePaymentStatus(payment, 'paid')}
                          title="Ödendi olarak işaretle"
                        >
                          ✓
                        </button>
                      )}
                      <button
                        className="list-action-btn edit-btn"
                        onClick={() => openEditModal(payment)}
                        title="Düzenle"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        className="list-action-btn delete-btn"
                        onClick={() => handleDelete(payment)}
                        title="Sil"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

          {getListFilteredPayments().length === 0 && (
            <div className="empty-list">
              <p>
                {listSearchTerm || listFilterCategory !== 'all' || listFilterMonth !== 'all'
                  ? 'Filtrelere uygun ödeme bulunamadı'
                  : 'Bu kategoride ödeme bulunmuyor'}
              </p>
              {(listSearchTerm || listFilterCategory !== 'all' || listFilterMonth !== 'all') && (
                <button className="filter-reset-btn" onClick={resetListFilters}>
                  Filtreleri Temizle
                </button>
              )}
            </div>
          )}
        </div>

        {/* Daha Fazla Göster */}
        {getListFilteredPayments().length > listDisplayLimit && (
          <div className="load-more-container">
            <button
              className="load-more-btn"
              onClick={() => setListDisplayLimit(prev => prev + 20)}
            >
              Daha Fazla Göster ({getListFilteredPayments().length - listDisplayLimit} ödeme daha)
            </button>
          </div>
        )}
      </div>

      {/* Masraflar Bölümü (Personnel sayfasından taşındı) */}
      <div className="expenses-section" style={{ margin: '0 30px 30px 30px', maxWidth: '1540px' }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 16px 0', fontSize: '1.2rem', color: '#1e293b' }}>
          <Wallet size={20} /> Masraflar
        </h2>

        {/* Filtreler */}
        <div className="expenses-filters">
          <div className="filter-group">
            <label><Calendar size={16} /> Başlangıç</label>
            <input
              type="date"
              value={expensesStartDate}
              onChange={(e) => setExpensesStartDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label><Calendar size={16} /> Bitiş</label>
            <input
              type="date"
              value={expensesEndDate}
              onChange={(e) => setExpensesEndDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label><Store size={16} /> Mağaza</label>
            <select
              value={expensesStoreFilter}
              onChange={(e) => setExpensesStoreFilter(e.target.value)}
            >
              <option value="all">Tüm Mağazalar</option>
              {stores.map(store => (
                <option key={store.id} value={store.id}>{store.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Mağaza Bazlı Özet */}
        <div className="expenses-summary">
          {stores.map(store => {
            const storeExpenses = allExpenses.filter(e => e.store_id === store.id);
            const storeTotal = storeExpenses.reduce((sum, e) => sum + Number(e.amount), 0);
            return (
              <div key={store.id} className="expense-store-card">
                <h4>{store.name}</h4>
                <div className="expense-total">₺{storeTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
                <div className="expense-count">{storeExpenses.length} masraf</div>
              </div>
            );
          })}
          <div className="expense-store-card total">
            <h4>Toplam</h4>
            <div className="expense-total">₺{allExpenses.reduce((sum, e) => sum + Number(e.amount), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</div>
            <div className="expense-count">{allExpenses.length} masraf</div>
          </div>
        </div>

        {/* Masraf Listesi */}
        {loadingExpenses ? (
          <div className="loading">Yükleniyor...</div>
        ) : allExpenses.length === 0 ? (
          <div className="empty-state">
            <Wallet size={48} style={{ color: '#9ca3af' }} />
            <p>Bu tarih aralığında masraf bulunamadı</p>
          </div>
        ) : (
          <div className="expenses-table">
            <div className="table-header">
              <span className="col-date">Tarih</span>
              <span className="col-store">Mağaza</span>
              <span className="col-description">Açıklama</span>
              <span className="col-category">Kategori</span>
              <span className="col-payment">Ödeme</span>
              <span className="col-amount">Tutar</span>
              <span className="col-user">Ekleyen</span>
            </div>
            <div className="table-body">
              {allExpenses.map(expense => (
                <div key={expense.id} className="table-row">
                  <span className="col-date">
                    {new Date(expense.created_at).toLocaleDateString('tr-TR')}
                    <small>{new Date(expense.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</small>
                  </span>
                  <span className="col-store">{expense.store_name || '-'}</span>
                  <span className="col-description">{expense.description}</span>
                  <span className="col-category">
                    <span className="category-badge">{expense.category}</span>
                  </span>
                  <span className="col-payment">
                    <span className={`payment-badge ${expense.payment_method?.toLowerCase().replace(' ', '-')}`}>
                      {expense.payment_method}
                    </span>
                  </span>
                  <span className="col-amount expense-amount">₺{Number(expense.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                  <span className="col-user">{expense.user_name || '-'}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Ödeme Modal */}
      {showModal && (
        <div className={`modal-overlay ${closingModal === 'main' ? 'closing' : ''}`}>
          <div className="modal payment-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingPayment ? 'Ödeme Düzenle' : 'Yeni Ödeme'}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('main', setShowModal)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Başlık *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                    placeholder="Ödeme başlığı..."
                  />
                </div>
                <div className="form-group">
                  <label>Tutar *</label>
                  <div className="amount-with-currency">
                    <PriceInput
                      value={formData.amount}
                      onChange={val => setFormData({...formData, amount: val})}
                      placeholder="0.00"
                    />
                    <select
                      className="currency-select"
                      value={formData.currency}
                      onChange={e => setFormData({...formData, currency: e.target.value})}
                    >
                      {Object.entries(currencyInfo).map(([code, info]) => (
                        <option key={code} value={code}>
                          {info.icon} {code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Tür</label>
                  <div className="type-buttons">
                    <button
                      className={`type-btn expense ${formData.payment_type === 'expense' ? 'active' : ''}`}
                      onClick={() => setFormData({...formData, payment_type: 'expense'})}
                    >
                      Gider
                    </button>
                    <button
                      className={`type-btn income ${formData.payment_type === 'income' ? 'active' : ''}`}
                      onClick={() => setFormData({...formData, payment_type: 'income'})}
                    >
                      Gelir
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>Kategori</label>
                  <select
                    value={formData.category}
                    onChange={e => handleCategoryChange(e.target.value)}
                  >
                    {Object.entries(categoryNames).map(([key, name]) => (
                      <option key={key} value={key}>
                        {categoryIcons[key]} {name} {autoRecurringCategories.includes(key) ? '(Aylık)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Alıcı / Kime</label>
                  <input
                    type="text"
                    value={formData.recipient}
                    onChange={e => setFormData({...formData, recipient: e.target.value})}
                    placeholder="Ödeme yapılacak kişi/kurum..."
                  />
                </div>
                <div className="form-group">
                  <label>Vade Tarihi *</label>
                  <input
                    type="date"
                    value={formData.due_date}
                    onChange={e => setFormData({...formData, due_date: e.target.value})}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Hatırlatma (gün önce)</label>
                  <select
                    value={formData.reminder_days}
                    onChange={e => setFormData({...formData, reminder_days: Number(e.target.value)})}
                  >
                    <option value={1}>1 gün önce</option>
                    <option value={3}>3 gün önce</option>
                    <option value={7}>1 hafta önce</option>
                    <option value={14}>2 hafta önce</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Durum</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="pending">Bekliyor</option>
                    <option value="paid">Ödendi</option>
                    <option value="cancelled">İptal</option>
                  </select>
                </div>
              </div>

              <div className="form-row recurring-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.recurring}
                      onChange={e => setFormData({...formData, recurring: e.target.checked})}
                    />
                    <span>Tekrarlayan Ödeme</span>
                  </label>
                  {autoRecurringCategories.includes(formData.category) && formData.recurring && (
                    <span className="auto-recurring-hint">
                      Bu kategori için otomatik olarak aylık tekrar ayarlandı
                    </span>
                  )}
                </div>
                {formData.recurring && (
                  <div className="form-group">
                    <label>Tekrar Sıklığı</label>
                    <select
                      value={formData.recurring_interval}
                      onChange={e => setFormData({...formData, recurring_interval: e.target.value})}
                    >
                      <option value="weekly">Haftalık</option>
                      <option value="monthly">Aylık</option>
                      <option value="yearly">Yıllık</option>
                    </select>
                  </div>
                )}
              </div>

              {formData.recurring && formData.due_date && (
                <div className="next-occurrence-preview">
                  <span className="preview-icon"><RefreshCw size={16} style={{ color: '#3b82f6' }} /></span>
                  <span className="preview-text">
                    Sonraki ödeme tarihi: {' '}
                    <strong>
                      {getNextOccurrence(formData.due_date, formData.recurring_interval).toLocaleDateString('tr-TR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </strong>
                  </span>
                </div>
              )}

              <div className="form-group">
                <label>Açıklama</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                  placeholder="Ödeme detayları..."
                  rows="2"
                />
              </div>

              <div className="form-group">
                <label>Notlar</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({...formData, notes: e.target.value})}
                  placeholder="Ek notlar..."
                  rows="2"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>İptal</button>
              <button className="btn-save" onClick={handleSave}>
                {editingPayment ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Log Modal */}
      <LogModal
        isOpen={showLogModal}
        onClose={() => setShowLogModal(false)}
        module="payments"
        title="Ödemeler"
        showEntityCode={false}
      />

      {/* Genel Onay Modal */}
      {showConfirmModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          animation: 'fadeIn 0.2s ease'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            width: '90%',
            maxWidth: '400px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            animation: 'slideUp 0.3s ease'
          }}>
            <div style={{
              background: confirmModalConfig.type === 'danger'
                ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                : confirmModalConfig.type === 'warning'
                  ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                  : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={24} color="white" />
              <h3 style={{
                margin: 0,
                color: 'white',
                fontSize: '18px',
                fontWeight: '600'
              }}>{confirmModalConfig.title}</h3>
            </div>
            <div style={{ padding: '24px' }}>
              <p style={{
                margin: '0 0 24px 0',
                color: '#4b5563',
                fontSize: '15px',
                lineHeight: '1.6'
              }}>{confirmModalConfig.message}</p>
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => setShowConfirmModal(false)}
                  style={{
                    padding: '10px 20px',
                    border: '1px solid #e5e7eb',
                    background: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    transition: 'all 0.2s'
                  }}
                >
                  {confirmModalConfig.cancelText}
                </button>
                <button
                  onClick={() => {
                    confirmModalConfig.onConfirm();
                    setShowConfirmModal(false);
                  }}
                  style={{
                    padding: '10px 20px',
                    border: 'none',
                    background: confirmModalConfig.type === 'danger'
                      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                      : confirmModalConfig.type === 'warning'
                        ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                        : 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                    transition: 'all 0.2s'
                  }}
                >
                  {confirmModalConfig.confirmText}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentCalendar;
